import {useEffect, useRef, useState} from 'react';
import type {LayersAnimationTiming} from './editable-text-layers-visual.types';

interface UseLayerMorphLoopParams {
  seamless: boolean;
  allSettled: boolean;
  timing: LayersAnimationTiming;
}

/**
 * Drives the recurring composed↔separated morph for the layers visual. Once every layer has settled
 * (and not in seamless mode) it flips `isSeparated` on the configured cadence via chained timers,
 * tearing them down on unmount or dependency change. Returns the current pose flag.
 */
export function useLayerMorphLoop({seamless, allSettled, timing}: UseLayerMorphLoopParams): boolean {
  const [isSeparated, setIsSeparated] = useState(false);
  const isSeparatedRef = useRef(false);

  useEffect(() => {
    if (seamless || !allSettled) {
      return undefined;
    }
    let restTimer: number | undefined;
    let morphTimer: number | undefined;
    const scheduleMorph = (): void => {
      const transition = isSeparatedRef.current ? timing.toComposed : timing.toSeparated;
      restTimer = window.setTimeout(() => {
        isSeparatedRef.current = !isSeparatedRef.current;
        setIsSeparated(isSeparatedRef.current);
        morphTimer = window.setTimeout(scheduleMorph, transition.durationS * 1000);
      }, transition.delayMs);
    };
    scheduleMorph();
    return (): void => {
      if (restTimer !== undefined) {
        window.clearTimeout(restTimer);
      }
      if (morphTimer !== undefined) {
        window.clearTimeout(morphTimer);
      }
    };
  }, [seamless, allSettled, timing]);

  return isSeparated;
}
